import { useMemo, useState } from 'react';
import {
  dedupePropertiesById,
  filterPropertiesByListingMode,
  getPropertyListingMode,
  isPropertyFullyFunded,
  propertyMatchesBrowseCategory,
  propertyMatchesBrowseSearch,
  propertyMatchesListedDateFilter,
  propertyVisibleOnSite,
} from '../utils/properties';
import { getRegionLabel, propertyMatchesCountry } from '../data/countries';
import PropertyCard from '../components/property/PropertyCard';
import PaginationBar from '../components/shared/PaginationBar';
import useListPagination from '../hooks/useListPagination';

const FundedPropertiesPage = ({
  catalogProperties,
  properties,
  favorites,
  compareList,
  onToggleFav,
  onToggleCompare,
  onSelectProperty,
  country,
  setPage,
}) => {
  const [filter, setFilter] = useState('All');
  const source = dedupePropertiesById(catalogProperties?.length ? catalogProperties : properties);

  const filtered = useMemo(() => {
    const saleList = filterPropertiesByListingMode(source, 'sale');
    return saleList.filter((p) =>
      propertyVisibleOnSite(p)
      && getPropertyListingMode(p) === 'sale'
      && isPropertyFullyFunded(p)
      && propertyMatchesCountry(p.country, country)
      && (filter === 'All' || p.type === filter || (filter === 'Land' && p.landBanking))
      && propertyMatchesBrowseSearch(p, '')
      && propertyMatchesListedDateFilter(p, { mode: 'all' })
    );
  }, [source, country, filter]);

  const paginationKey = `funded|${filter}|${country}|${filtered.length}`;
  const {
    items: pageItems,
    page,
    total,
    totalPages,
    startIndex,
    endIndex,
    hasPrev,
    hasNext,
    goToPage,
  } = useListPagination(filtered, paginationKey);

  return (
    <div className="container listings-section" style={{ paddingTop: '2rem' }} data-listings-anchor>
      <div className="section-header">
        <div>
          <h2 className="section-title">Fully funded properties — {getRegionLabel(country)}</h2>
          <p style={{ color: 'var(--text-light)', maxWidth: '720px' }}>
            These offerings have completed primary funding. They are shown here for reference only.
            To buy shares, use the{' '}
            <button type="button" style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }} onClick={() => setPage('marketplace')}>
              Secondary Marketplace
            </button>{' '}
            when other investors list shares for sale.
          </p>
        </div>
        <div className="filters">
          {['All', 'Residential', 'Commercial', 'Vacation', 'Land'].map((f) => (
            <button key={f} type="button" className={`filter-btn ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 1rem', background: 'var(--card)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
          <i className="fas fa-check-circle" style={{ fontSize: '2.5rem', color: 'var(--success)', marginBottom: '1rem' }}></i>
          <h3 style={{ marginBottom: '0.5rem' }}>No fully funded properties in {getRegionLabel(country)}</h3>
          <p style={{ color: 'var(--text-light)' }}>
            Browse active primary offerings on the home page or check the marketplace for secondary share listings.
          </p>
        </div>
      ) : (
        <>
          <div className="properties-grid">
            {pageItems.map((p) => (
              <PropertyCard
                key={p.id}
                p={p}
                country={country}
                onClick={() => onSelectProperty(p)}
                isFavorite={favorites.includes(p.id)}
                onToggleFav={onToggleFav}
                isComparing={compareList.some((c) => String(c.id) === String(p.id))}
                onToggleCompare={onToggleCompare}
              />
            ))}
          </div>
          <PaginationBar
            page={page}
            totalPages={totalPages}
            total={total}
            startIndex={startIndex}
            endIndex={endIndex}
            hasPrev={hasPrev}
            hasNext={hasNext}
            onPageChange={goToPage}
            itemLabel="properties"
          />
        </>
      )}
    </div>
  );
};

export default FundedPropertiesPage;
